'use client';

import { c as _c } from "react/compiler-runtime";
import { pushEvent } from '../pushEvent';
import { AnalyticsPageAttributes } from '../helpers/getAnalyticsPageAttributes';
import { useEffect, useRef } from 'react';
import { getCleanDataLayerEvent } from '../helpers/getCleanDataLayerEvent';
import { useMeasureReadingIntensity } from '@/globals/analytics/readingIntensityAnalytics';
import { useReferrer, useVisitedPartners } from '@/globals/pageWrappers/usePageHistory';
import { useIncrementFeedbackPageCount } from '@/sections/pageFeedback/usePageFeedbackPopup';
import { usePathname } from 'next/navigation';
import { WebAPIMetadata } from '@/globals/webApiTypes/elements/WebAPIMetadata';
type Partners = WebAPIMetadata['op:partner'];
const pushPageviewEvent = ({
  analyticsPageAttributes,
  partners = [],
  visitedPartners = new Set(),
  referrer = '',
  pathname,
  colorScheme
}: {
  analyticsPageAttributes: AnalyticsPageAttributes;
  partners: Partners;
  visitedPartners: Set<string> | undefined;
  referrer: string | undefined;
  pathname: string;
  colorScheme: 'Dark' | 'Light' | 'Unknown';
}) => {
  if (referrer !== '') pushEvent(getCleanDataLayerEvent());
  pushEvent({
    events: {
      category: 'interaction.pageview'
    },
    page: {
      ...analyticsPageAttributes,
      urlPath: analyticsPageAttributes.urlPath ?? pathname,
      referrer
    },
    user: {
      opPartnerList: [...new Set([...visitedPartners, ...partners])],
      colorScheme
    }
  });
};
const getColorScheme = () => {
  if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'Dark';
  } else if (window.matchMedia('(prefers-color-scheme: light)').matches) {
    return 'Light';
  }
  return 'Unknown';
};
const usePushPageviewEvent = t0 => {
  const $ = _c(7);
  const {
    analyticsPageAttributes,
    partners
  } = t0;
  const savedForPageRef = useRef(undefined);
  const pathname = usePathname();
  const visitedPartners = useVisitedPartners();
  const referrer = useReferrer();
  let t1;
  let t2;
  if ($[0] !== analyticsPageAttributes || $[1] !== partners || $[2] !== pathname || $[3] !== referrer || $[4] !== visitedPartners) {
    t1 = () => {
      if (savedForPageRef.current === pathname) {
        return;
      }
      savedForPageRef.current = pathname;
      pushPageviewEvent({
        analyticsPageAttributes,
        partners,
        visitedPartners,
        referrer,
        pathname,
        colorScheme: getColorScheme()
      });
    };
    t2 = [analyticsPageAttributes, partners, pathname, referrer, visitedPartners];
    $[0] = analyticsPageAttributes;
    $[1] = partners;
    $[2] = pathname;
    $[3] = referrer;
    $[4] = visitedPartners;
    $[5] = t1;
    $[6] = t2;
  } else {
    t1 = $[5];
    t2 = $[6];
  }
  useEffect(t1, t2);
};

/**
 * Should come before other components to make sure
 * the pageview is counted before other effects
 */
export const LogPageviewEvent = t0 => {
  const $ = _c(6);
  const {
    analyticsPageAttributes,
    partners,
    wordCount,
    url
  } = t0;
  useIncrementFeedbackPageCount();
  let t1;
  if ($[0] !== analyticsPageAttributes || $[1] !== partners) {
    t1 = {
      analyticsPageAttributes,
      partners
    };
    $[0] = analyticsPageAttributes;
    $[1] = partners;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  usePushPageviewEvent(t1);
  let t2;
  if ($[3] !== url || $[4] !== wordCount) {
    t2 = {
      urlPath: url,
      wordCount
    };
    $[3] = url;
    $[4] = wordCount;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  useMeasureReadingIntensity(t2);
  return null;
};